import { useCallback, useEffect, useState } from 'react'
import { useConfigUI } from '../context'

/**
 * SCRUM-1032 QC item 6 — a config drawer form that will not be dismissed by
 * accident.
 *
 * Two behaviours, from Wei Woon's report on the Valve & Pump form:
 *
 *  - the drawer closes only from its own Cancel button, never from a backdrop
 *    click or Escape, because both throw away everything typed so far;
 *  - Cancel on a form with unsaved edits asks first.
 *
 * The lock is released on unmount, so a view that crashes or is replaced cannot
 * leave the drawer permanently stuck open.
 *
 * @param isDirty whether the form holds edits that would be lost.
 */
const useSidebarCloseGuard = (isDirty: boolean) => {
  const { closeSidebar, lockSidebarDismiss } = useConfigUI()
  const [isConfirmOpen, setConfirmOpen] = useState(false)

  useEffect(() => {
    lockSidebarDismiss(true)
    return () => lockSidebarDismiss(false)
  }, [lockSidebarDismiss])

  /** Cancel was pressed: ask when there is something to lose, else just close. */
  const requestClose = useCallback(() => {
    if (isDirty) {
      setConfirmOpen(true)
      return
    }
    closeSidebar()
  }, [isDirty, closeSidebar])

  /** The user chose to discard. Drop the lock first — closing is now intended. */
  const confirmClose = useCallback(() => {
    setConfirmOpen(false)
    lockSidebarDismiss(false)
    closeSidebar()
  }, [closeSidebar, lockSidebarDismiss])

  const cancelClose = useCallback(() => setConfirmOpen(false), [])

  return { requestClose, confirmClose, cancelClose, isConfirmOpen }
}

export default useSidebarCloseGuard
